import { router, usePage } from "@inertiajs/react";
import { useDebouncedCallback } from "@ui/hooks/use-debounced-callback";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildQueryString, mergeOwnedParams, splitUrl } from "@/lib/search-params";

export interface UseSearchUrlSyncOptions {
  /** Current owned params. Empty/undefined values are dropped. */
  params: Readonly<Record<string, string | undefined>>;
  /** Every key this page owns, in canonical order. Owned keys absent from
   *  `params` are removed from the URL; foreign params (UTM tags, tab state)
   *  survive untouched. Include superseded legacy keys so they are stripped
   *  on the first write instead of coexisting with their v2 spellings. */
  ownedKeys: readonly string[];
  /** Keys whose value changes continuously (typing, dragging). A change
   *  confined to these is debounced — Safari caps `replaceState` at ~100
   *  calls per 30s and Inertia downgrades the resulting SecurityError to a
   *  console line, so an undebounced writer silently stops updating the URL.
   *  Any other change writes immediately. */
  continuousKeys?: readonly string[];
  debounceMs?: number;
}

type OwnedEntries = ReadonlyArray<readonly [string, string]>;

function toOwnedEntries(
  params: Readonly<Record<string, string | undefined>>,
  ownedKeys: readonly string[],
): OwnedEntries {
  const entries: [string, string][] = [];
  for (const key of ownedKeys) {
    const value = params[key];
    if (value) entries.push([key, value]);
  }
  return entries;
}

/** The mount URL's owned params, raw and in URL order — equal to the canonical
 *  serialization only when the URL is already canonical, so a legacy or
 *  hand-typed URL triggers exactly one normalizing replace. */
function mountOwnedState(url: string, ownedKeys: readonly string[]): { slice: string; entries: OwnedEntries } {
  const ownedSet = new Set(ownedKeys);
  const entries: [string, string][] = [];
  splitUrl(url).params.forEach((value, key) => {
    if (ownedSet.has(key)) entries.push([key, value]);
  });
  return { slice: buildQueryString(entries), entries };
}

function changedKeys(previous: OwnedEntries, current: OwnedEntries): string[] {
  const previousMap = new Map(previous);
  const currentMap = new Map(current);
  const keys = new Set([...previousMap.keys(), ...currentMap.keys()]);
  return [...keys].filter((key) => previousMap.get(key) !== currentMap.get(key));
}

/**
 * Continuously mirrors a search page's state into the URL so every state has a
 * shareable address. Client-only by design: search results come from the
 * `_msearch` XHR proxy, so the server never needs these params — writes go
 * through `router.replace` (never raw `history.replaceState`, which would
 * desync Inertia's page/history state; see `useConversationFilters`), create
 * no history entries, and preserve scroll and component state.
 *
 * The read path is the page's own mount-time parse — this hook only writes.
 */
export function useSearchUrlSync({
  params,
  ownedKeys,
  continuousKeys,
  debounceMs = 400,
}: UseSearchUrlSyncOptions): void {
  const page = usePage();
  const entries = toOwnedEntries(params, ownedKeys);
  const serialized = buildQueryString(entries);

  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const urlRef = useRef(page.url);
  urlRef.current = page.url;
  const ownedKeysRef = useRef(ownedKeys);
  ownedKeysRef.current = ownedKeys;
  const continuousKeysRef = useRef(continuousKeys);
  continuousKeysRef.current = continuousKeys;

  const [mountState] = useState(() => mountOwnedState(page.url, ownedKeys));
  const lastWrittenRef = useRef(mountState.slice);
  const lastEntriesRef = useRef(mountState.entries);

  // Identity-stable so the debounced wrapper never re-memoizes mid-flight (a
  // pending timer on a stale closure would fire with stale state). All inputs
  // are read from refs at call time, including the current URL, so a
  // concurrent foreign-param change is merged rather than clobbered.
  const write = useCallback(() => {
    const next = buildQueryString(entriesRef.current);
    if (next === lastWrittenRef.current) return;
    router.replace({
      preserveScroll: true,
      preserveState: true,
      url: mergeOwnedParams(urlRef.current, entriesRef.current, ownedKeysRef.current),
    });
    lastWrittenRef.current = next;
    lastEntriesRef.current = entriesRef.current;
  }, []);

  const debouncedWrite = useDebouncedCallback(write, debounceMs);

  useEffect(() => {
    if (serialized === lastWrittenRef.current) return;
    const changed = changedKeys(lastEntriesRef.current, entriesRef.current);
    const continuous = continuousKeysRef.current ?? [];
    if (changed.length > 0 && changed.every((key) => continuous.includes(key))) {
      debouncedWrite();
    } else {
      debouncedWrite.cancel();
      write();
    }
  }, [serialized, write, debouncedWrite]);
}
